/**
 * daiAdapters.ts — normalizes each domain's raw backend envelope into the flat
 * row shape the existing tables/columns/summary-card logic expects.
 *
 * NOTE: the backend's /silver, /gold and /iberia status endpoints already
 * return flat, row-shaped `items` (Silver/Gold) or nested `datasets`
 * (Iberia) — NOT an `Asset[]` list as the dai_node_ref reference design
 * assumed. toDashboardView used to map through a non-existent `assets`
 * field, which silently produced empty tables on every tab. It now passes
 * each domain's real items straight through.
 */

export type SlaStatus =
	| 'success' | 'recovered' | 'succeeded' | 'in_progress' | 'delayed' | 'delayed_ok'
	| 'delayed_running' | 'missed' | 'missed_sla' | 'failed'
	| 'scheduled' | 'cancelled' | 'unknown';

/**
 * Flat row shape the existing tables/columns already expect.
 * All fields optional except ones the table absolutely requires.
 */
export interface DashboardRow {
	data_asset: string;
	job_name?: string;
	market: string;
	gdo: string;
	schema?: string;
	application?: string;
	status: SlaStatus;
	status_message?: string;
	status_detail?: string;
	job_status?: string;
	expected_time?: string;
	expected_start?: string;
	expected_end?: string;
	hist_avg_start?: string;
	hist_avg_end?: string;
	csv_expected?: boolean;
	actual_start?: string;
	actual_end?: string;
	duration?: string;
	duration_mins?: number;
	run_count: number;
	failed_count: number;
	trigger_type?: string;
	critical: boolean;
	error_message?: string;
}

/**
 * Dashboard view — the shape all three pages render from.
 * Mirrors the legacy `{ items, summary, date, day, db_live, last_refresh }` envelope.
 */
export interface DashboardView {
	date: string;
	day?: string;
	items: DashboardRow[];
	summary: Record<string, number>;
	db_live?: boolean;
	last_refresh?: string;
}

const SUMMARY_BUCKETS: SlaStatus[] = [
	'success', 'delayed', 'missed_sla', 'in_progress',
	'missed', 'failed', 'scheduled'
];

/**
 * Build a summary count object from rows.
 * Keys match the legacy summary shape: 'success', 'delayed', 'missed_sla', etc.
 * Plus computed aggregates: 'completed' = success + delayed + missed_sla.
 */
export function buildSummary(rows: DashboardRow[]): Record<string, number> {
	const s: Record<string, number> = { total: rows.length };
	for (const b of SUMMARY_BUCKETS) {
		s[b] = rows.filter((r) => r.status === b).length;
	}
	// Legacy aliases
	s.inprog = s.in_progress ?? 0;
	s.completed = (s.success ?? 0) + (s.delayed ?? 0) + (s.missed_sla ?? 0);
	return s;
}

/**
 * Build a DashboardView from a domain's already-flat `items` array (Silver/Gold)
 * plus optional real backend metadata (falls back to "now"/unknown-live if omitted).
 * Iberia's nested `datasets` are NOT row-shaped — Iberia.tsx's own toIberiaRows
 * flattens those separately rather than calling this function on raw datasets.
 */
export function toDashboardView(
	date: string,
	items: any[] | undefined,
	meta?: { last_refresh?: string; db_live?: boolean }
): DashboardView {
	const rows = (items ?? []) as DashboardRow[];
	return {
		date,
		items: rows,
		summary: buildSummary(rows),
		last_refresh: meta?.last_refresh ?? new Date().toISOString(),
		db_live: meta?.db_live ?? false
	};
}

/**
 * Map `SlaStatus` code to a label for `StatusChip` component
 * (StatusChip looks up color by `status.toUpperCase()`, so these must match its enum).
 */
export const STATUS_CHIP_LABEL: Record<SlaStatus, string> = {
	success: 'Completed',     // → green
	recovered: 'Completed',
	succeeded: 'Completed',   // defensive: raw DB value if timing_status is null
	delayed_ok: 'Delayed',       // → orange
	delayed: 'Delayed',
	delayed_running: 'Long Running',  // → orange
	in_progress: 'In Progress',   // → amber
	missed_sla: 'SLA Missed',    // → red
	missed: 'Not Started',   // → dark grey
	failed: 'Failed',        // → red
	scheduled: 'Scheduled',     // → grey
	cancelled: 'N/A',
	unknown: 'N/A'
};
